import React, { useEffect, useState, useContext } from "react"
import { Box, Divider, Snackbar, Alert, Link } from "@carrotfertility/carotene-core"
import { BiometricAuthenticationSettings } from "#/components/views/account/BiometricAuthenticationSettings"
import usePartnerAccess, { useUserRole } from "#/components/partner-access/hooks/usePartnerAccess"
import {
  PartnerInformationSettings,
  PartnerInformationSettingsPrompt
} from "#/components/views/account/PartnerSettings/PartnerInformationSettings"
import usePartnerInformation from "#/components/views/account/hooks/usePartnerInformation"
import { PersonalInformationSettings } from "#/components/views/account/PersonalInformationSettings"
import { PartnerAccessAccountSettingsFooter } from "#/components/partner-access/member-account-settings/PartnerAccessAccountSettingsFooter"
import { AddressSettings } from "#/components/views/account/AddressSettings"
import EmailAddressesAccountSettings from "#/components/EmailVerification/EmailAddressesAccountSettings"
import PasswordSettings from "#/components/views/account/PasswordSettings"
import { AccountDeletion } from "#/components/views/account/AccountDeletion"
import { ExternalStorefrontSubscriptionSettings } from "#/components/views/account/ExternalStorefrontSubscriptionSettings"
import { FormattedMessage } from "react-intl"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { PageHeader, PageHeaderGlyph } from "#/features/global-ui/page-header"
import { PageLayout } from "#/features/global-ui/page-layout"
import { FormButton, useFormContext } from "@carrotfertility/carotene-core-x"
import { CommunicationSettings } from "#/components/views/account/CommunicationSettings"
import { useTextDirection } from "#/services/locale/hooks/useTextDirection"
import { TextDirection } from "#/types/Locale"
import useGetBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import { useFlags } from "launchdarkly-react-client-sdk"
import { PartnerInformationSettingsPartnerSupport } from "#/components/views/account/PartnerSettings/PartnerInformationSettingsPartnerSupport"
import { SettingsContext } from "./index"
import { useGetMemberIdInfo } from "#/components/member-id/hooks/useGetMemberIdInfo"
import { PartnerEligibilityStatus } from "#/utils/PartnerEligibilityStatus"
import { DbgPaths, Paths } from "#/utils/Paths"

export function SettingsPageDivider(): JSX.Element {
  return (
    <Box margin={(theme) => theme.spacing(theme.tokens.spacing.xxl, theme.tokens.spacing.none)}>
      <Divider />
    </Box>
  )
}

export function SettingsPage(): JSX.Element {
  const { isPosingAsMember } = useCurrentUser()
  const benefit = useGetBenefit()
  const isPartnerEnabled = benefit?.data?.generalSupport?.showPartnerContent
  const {
    formState: { isDirty }
  } = useFormContext()
  const { hasProvidedAnyPartnerInfo } = usePartnerInformation()
  const { partnerEligibilityStatus } = useGetMemberIdInfo()
  const [showPartnerInfo, setShowPartnerInfo] = useState(hasProvidedAnyPartnerInfo)
  const { isPartnerAccessingAccount } = useUserRole()
  const { showPartnerAccess } = usePartnerAccess()
  const textDirection = useTextDirection()
  const { carrotConnectEnabled } = useFlags()
  const { showPartnerFieldsSuccessAlert, setShowPartnerFieldsSuccessAlert } = useContext(SettingsContext)

  useEffect(() => {
    setShowPartnerInfo(hasProvidedAnyPartnerInfo)
  }, [hasProvidedAnyPartnerInfo])

  function handleShowPartnerInfoClick() {
    setShowPartnerInfo(true)
  }

  return (
    <PageLayout
      variant="form"
      header={
        <PageHeader
          startGlyph={
            <PageHeaderGlyph src="/images/icn-account-settings.png" srcSet={"/images/icn-account-settings-2x.png"} />
          }
          pageTitle={<FormattedMessage defaultMessage="Account settings" />}
        />
      }
    >
      <Box>
        {carrotConnectEnabled &&
          showPartnerFieldsSuccessAlert &&
          partnerEligibilityStatus === PartnerEligibilityStatus.ELIGIBLE && (
            <Alert
              severity="success"
              sx={(theme) => ({
                marginBlockEnd: theme.spacing(theme.tokens.spacing.lg),
                marginBlockStart: theme.spacing(theme.tokens.spacing.md)
              })}
              onClose={() => setShowPartnerFieldsSuccessAlert(false)}
            >
              <FormattedMessage
                defaultMessage="Account settings saved. Your partner has also been added to your <link>member ID</link> card."
                values={{
                  link: (linkContent) => (
                    <Link
                      target="_blank"
                      href={Paths.MEMBER_ID}
                      sx={(theme) => ({
                        color: theme.palette.success.main,
                        textDecorationColor: theme.palette.success.main
                      })}
                    >
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </Alert>
          )}
        {carrotConnectEnabled &&
          showPartnerFieldsSuccessAlert &&
          partnerEligibilityStatus === PartnerEligibilityStatus.NOT_ELIGIBLE && (
            <Alert
              severity="success"
              sx={(theme) => ({
                marginBlockEnd: theme.spacing(theme.tokens.spacing.lg),
                marginBlockStart: theme.spacing(theme.tokens.spacing.md)
              })}
              onClose={() => setShowPartnerFieldsSuccessAlert(false)}
            >
              <FormattedMessage
                defaultMessage="Account settings were saved. However, your partner is not eligible for coverage and savings at partner providers. Visit your <link>benefit guide</link> for more information."
                values={{
                  link: (linkContent) => (
                    <Link
                      target="_blank"
                      href={DbgPaths.CARROT_ELIGIBLE}
                      sx={(theme) => ({
                        color: theme.palette.success.main,
                        textDecorationColor: theme.palette.success.main
                      })}
                    >
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </Alert>
          )}
        <PersonalInformationSettings />
        <SettingsPageDivider />
        <AddressSettings />
        <SettingsPageDivider />
        <ExternalStorefrontSubscriptionSettings />
        <BiometricAuthenticationSettings />
        <EmailAddressesAccountSettings />
        <SettingsPageDivider />
        <AccountDeletion />
        {!isPartnerAccessingAccount && (
          <>
            <PasswordSettings isPartnerSection={false} />
            <SettingsPageDivider />
          </>
        )}
        <CommunicationSettings />
        <SettingsPageDivider />
        {isPartnerEnabled && carrotConnectEnabled ? (
          <PartnerInformationSettingsPartnerSupport />
        ) : (
          <>
            {isPartnerEnabled && showPartnerInfo && (
              <>
                <PartnerInformationSettings />
              </>
            )}
          </>
        )}

        <SettingsPageDivider />
        <Box marginBottom={(theme) => theme.spacing(theme.tokens.spacing.xxl)} display="flex" justifyContent="flex-end">
          <FormButton disabled={!isDirty || isPosingAsMember} type="submit">
            <FormattedMessage defaultMessage="Save changes" />
          </FormButton>
        </Box>
        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: textDirection === TextDirection.RightToLeft ? "right" : "left"
          }}
          open={isPosingAsMember}
          message={
            "Editing account settings while posing as a member is not allowed. If you need to update member info, please do so in the Admin app."
          }
        />
      </Box>
      {!carrotConnectEnabled && isPartnerEnabled && (
        <>{!showPartnerInfo && <PartnerInformationSettingsPrompt onClick={handleShowPartnerInfoClick} />}</>
      )}
      {showPartnerAccess && !isPartnerAccessingAccount && (
        <PartnerAccessAccountSettingsFooter addPartnerInfo={showPartnerInfo} />
      )}
    </PageLayout>
  )
}
